import { useExtensionAuthFlow } from "@web/hooks/useExtensionAuth"
import { getSession, signOut } from "next-auth/react"
import { CHROME_EXTENSION_ID } from "platform-common/config"
import { BrowserMessageType, SecureBrowserMessage } from "platform-common/types"
import { ExtensionStatus } from "platform-common/types/extension"
import { PropsWithChildren, useEffect, useCallback, createContext, useContext, useState } from "react"

export interface WindowMessageContextValue {
  beganExtensionAuth: boolean
  setBeganExtensionAuth: (beganExtensionAuth: boolean) => void
}

export const WindowMessageContext = createContext<WindowMessageContextValue>({
  beganExtensionAuth: false,
  setBeganExtensionAuth: () => {},
})

/**
 * Handle window messages sent from content scripts or browser messages.
 */
export const WindowMessageProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { beginExtensionAuthFlow } = useExtensionAuthFlow()
  const [beganExtensionAuth, setBeganExtensionAuth] = useState(false)

  const handleWindowMessage = useCallback(
    async (event: MessageEvent) => {
      // console.debug("WindowMessageProvider got window event:", event.data)

      const type = event.data.message as BrowserMessageType
      switch (type) {
        case BrowserMessageType.ExtensionSignOut:
          // signed out from extension; we should sign out too
          await signOut()
          break

        case BrowserMessageType.GotExtensionStatus:
          const result: ExtensionStatus = event.data.extensionStatus
          const { isAuthenticated, shouldAuthenticate } = result
          const session = await getSession()
          // if web is logged in and extension is not
          // send auth challenge to extension
          if (!session || isAuthenticated || !shouldAuthenticate) {
            return
          }

          // give extension token to authenticate itself
          try {
            setBeganExtensionAuth(true)
            await beginExtensionAuthFlow(session)
            setBeganExtensionAuth(false)
          } catch (err) {
            console.error("Failed to begin extension auth flow:", err)
          }
          break

        // ignore
        case BrowserMessageType.GetExtensionStatus:
        case BrowserMessageType.RestoreLastActiveTab:
        case BrowserMessageType.CloseTab:
          break

        default:
          if (type) console.debug("Unhandled message from extension:", type)
      }
    },
    [beginExtensionAuthFlow]
  )

  useEffect(() => {
    window.addEventListener("message", handleWindowMessage)
    return () => window.removeEventListener("message", handleWindowMessage)
  }, [handleWindowMessage])

  return (
    <WindowMessageContext.Provider value={{ beganExtensionAuth, setBeganExtensionAuth }}>
      {children}
    </WindowMessageContext.Provider>
  )
}

export const useWindowMessageContext = () => useContext(WindowMessageContext)

// connect to service worker securely (using extension id) and send a message
export async function sendMessageSecure(msg: SecureBrowserMessage): Promise<void> {
  const port = chrome.runtime.connect(CHROME_EXTENSION_ID)
  port.postMessage(msg)

  // expects other end to disconnect after processing the message
}
